import { useEffect, useMemo, useState } from 'react';
import {
  safelyGetFromLocalStorage,
  safelySetIntoLocalStorage,
} from 'App/storage';
import useStoreInfo from './useStoreInfo';

interface UseInstallationStatusAlertVisibilityParams {
  messageCode?: string | null;
  status?: string | null;
}

interface UseInstallationStatusAlertVisibilityResult {
  isVisible: boolean;
  dismiss: () => void;
}

const STORAGE_PREFIX = 'mxInstallationStatusAlertDismissed';

function getMessageKey(params: UseInstallationStatusAlertVisibilityParams) {
  return params.messageCode ?? params.status ?? '';
}

export function useInstallationStatusAlertVisibility(
  params: UseInstallationStatusAlertVisibilityParams,
): UseInstallationStatusAlertVisibilityResult {
  const { storeInfo } = useStoreInfo();
  const { messageCode, status } = params;

  const storageKey = useMemo(() => {
    const storeId = storeInfo?.id ?? '';
    const messageKey = getMessageKey({ messageCode, status });
    return `${STORAGE_PREFIX}:${storeId}:${messageKey}`;
  }, [messageCode, status, storeInfo?.id]);

  const [isVisible, setIsVisible] = useState(
    () => safelyGetFromLocalStorage(storageKey) !== 'true',
  );

  useEffect(() => {
    const isDismissed = safelyGetFromLocalStorage(storageKey) === 'true';
    setIsVisible(!isDismissed);
  }, [storageKey]);

  const dismiss = () => {
    safelySetIntoLocalStorage(storageKey, 'true');
    setIsVisible(false);
  };

  return { isVisible, dismiss };
}
